import config from "config";

export interface DropboxFile {
  bytes: number;
  icon: string;
  id: string;
  isDir: boolean;
  link: string;
  linkType: string;
  name: string;
  thumbnailLink: string;
}

const DROPBOX_SCRIPT_SRC = "https://www.dropbox.com/static/api/2/dropins.js";
const DROPBOX_SCRIPT_ID = "dropboxjs";
const DROPBOX_READY_TIMEOUT_MS = 10_000;
const DROPBOX_READY_POLL_INTERVAL_MS = 100;

let dropboxReadyPromise: Promise<void> | null = null;

type DropboxChooseOptions = {
  success(files: DropboxFile[]): void;
  extensions: string[];
  linkType: "direct";
  multiselect: boolean;
};

type DropboxWindow = Window & {
  Dropbox?: {
    choose(options: DropboxChooseOptions): void;
  };
};

function getDropboxWindow() {
  return window as DropboxWindow;
}

const hasDropboxChoose = () =>
  typeof getDropboxWindow().Dropbox?.choose === "function";

const getOrCreateDropboxScript = (): HTMLScriptElement => {
  const existingScript =
    document.querySelector<HTMLScriptElement>(`script#${DROPBOX_SCRIPT_ID}`) ??
    document.querySelector<HTMLScriptElement>(
      `script[src="${DROPBOX_SCRIPT_SRC}"]`
    );

  if (existingScript) {
    return existingScript;
  }

  const script = document.createElement("script");
  script.id = DROPBOX_SCRIPT_ID;
  script.src = DROPBOX_SCRIPT_SRC;
  script.dataset.appKey = config.dropboxAppKey;
  script.async = true;
  script.defer = true;
  document.head.append(script);

  return script;
};

const waitForDropboxReady = () =>
  new Promise<void>((resolve, reject) => {
    if (hasDropboxChoose()) {
      resolve();
      return;
    }

    const start = Date.now();
    const interval = window.setInterval(() => {
      if (hasDropboxChoose()) {
        window.clearInterval(interval);
        resolve();
        return;
      }

      if (Date.now() - start >= DROPBOX_READY_TIMEOUT_MS) {
        window.clearInterval(interval);
        reject(new Error("Dropbox picker is unavailable"));
      }
    }, DROPBOX_READY_POLL_INTERVAL_MS);
  });

const ensureDropboxReady = async () => {
  if (hasDropboxChoose()) {
    return;
  }

  if (!dropboxReadyPromise) {
    dropboxReadyPromise = (async () => {
      getOrCreateDropboxScript();
      await waitForDropboxReady();
    })().catch((error) => {
      dropboxReadyPromise = null;
      throw error;
    });
  }

  await dropboxReadyPromise;
};

// const options = { responseType: "blob" };
export async function selectFileFromDropBox(): Promise<Array<DropboxFile>> {
  await ensureDropboxReady();

  return new Promise((resolve, reject) => {
    const dropbox = getDropboxWindow().Dropbox;

    if (!dropbox?.choose) {
      reject(new Error("Dropbox picker is unavailable"));
      return;
    }

    dropbox.choose({
      success: (files) => resolve(files),
      extensions: ["images", "video"],
      linkType: "direct",
      multiselect: true,
    });
  });
}
